import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { ChevronRight, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

function slugify(s: string): string {
  return s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

const TOP_LIMIT = 5;

export default function PartsCatalogSidebar() {
  const isMobile = useIsMobile();
  const [catsOpen, setCatsOpen] = useState(false);
  const [mfrsOpen, setMfrsOpen] = useState(false);
  const showCats = !isMobile || catsOpen;
  const showMfrs = !isMobile || mfrsOpen;
  const { data: cats = [] } = useQuery({
    queryKey: ["parts-sidebar-categories"],
    queryFn: async () => {
      // Prefer featured, fall back to all active by sort order
      const featured = await supabase
        .from("parts_categories")
        .select("basic_category, display_name, slug, sort_order, is_featured_home")
        .eq("is_active", true)
        .eq("is_featured_home", true)
        .order("sort_order", { ascending: true })
        .limit(TOP_LIMIT);
      if (featured.data && featured.data.length >= TOP_LIMIT) return featured.data;
      const all = await supabase
        .from("parts_categories")
        .select("basic_category, display_name, slug, sort_order, is_featured_home")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("display_name", { ascending: true })
        .limit(TOP_LIMIT);
      return all.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: mfrs = [] } = useQuery({
    queryKey: ["parts-sidebar-manufacturers"],
    queryFn: async () => {
      const featured = await supabase
        .from("parts_manufacturers")
        .select("vendor_id, name, slug, sort_order, is_featured_home")
        .eq("is_active", true)
        .eq("is_featured_home", true)
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true })
        .limit(TOP_LIMIT);
      if (featured.data && featured.data.length >= TOP_LIMIT) return featured.data;
      const all = await supabase
        .from("parts_manufacturers")
        .select("vendor_id, name, slug, sort_order, is_featured_home")
        .eq("is_active", true)
        .order("name", { ascending: true })
        .limit(TOP_LIMIT);
      return all.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  return (
    <aside className="space-y-6">
      <div>
        <button
          type="button"
          onClick={() => setCatsOpen((v) => !v)}
          className="w-full flex items-center justify-between mb-2 md:cursor-default"
          aria-expanded={showCats}
        >
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Categories
          </h2>
          <ChevronDown
            className={`md:hidden w-4 h-4 text-muted-foreground transition-transform ${catsOpen ? "rotate-180" : ""}`}
          />
        </button>
        {showCats && (cats.length === 0 ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : (
          <ul className="space-y-1">
            {cats.map((c) => (
              <li key={c.basic_category}>
                <Link
                  to={`/parts-catalog/category/${c.slug || slugify(c.basic_category)}`}
                  className="block w-full text-left px-2 py-1.5 rounded-sm text-sm transition-colors hover:bg-muted"
                >
                  <span className="truncate">{c.display_name || c.basic_category}</span>
                </Link>
              </li>
            ))}
          </ul>
        ))}
        {showCats && (
          <Link
            to="/parts-catalog/categories"
            className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline px-2"
          >
            View all categories <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      <div>
        <button
          type="button"
          onClick={() => setMfrsOpen((v) => !v)}
          className="w-full flex items-center justify-between mb-2 md:cursor-default"
          aria-expanded={showMfrs}
        >
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Manufacturers
          </h2>
          <ChevronDown
            className={`md:hidden w-4 h-4 text-muted-foreground transition-transform ${mfrsOpen ? "rotate-180" : ""}`}
          />
        </button>
        {showMfrs && (mfrs.length === 0 ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : (
          <ul className="space-y-1">
            {mfrs.map((m) => (
              <li key={m.vendor_id}>
                <Link
                  to={
                    m.slug
                      ? `/parts-catalog/manufacturer/${m.slug}`
                      : `/parts-catalog/results?vendor=${encodeURIComponent(m.name.toUpperCase())}`
                  }
                  className="block w-full text-left px-2 py-1.5 rounded-sm text-sm transition-colors hover:bg-muted"
                >
                  <span className="truncate">{m.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        ))}
        {showMfrs && (
          <Link
            to="/parts-catalog/manufacturers"
            className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline px-2"
          >
            View all manufacturers <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
    </aside>
  );
}